import type { BenefitDetailDto, BenefitEligibilityDto, ServiceReceivedDto } from "./benefit-types"
import { getBenefitProgramLabel } from "./benefit-enums"

export interface ProgramDetailRow {
  key: string
  programId: number
  name: string
  request: "Requested" | "No saved request" | "Request unavailable"
  calculation: BenefitEligibilityDto | null
}

/** Saved intent, determinations and grants are distinct sources; never infer one from another. */
export function programDetailRows(benefit: Pick<BenefitDetailDto, "requestedBenefits" | "requestedBenefitsReadUnavailable" | "benefitEligibility" | "benefitGrants">): ProgramDetailRow[] {
  const programs = new Set([
    ...benefit.requestedBenefits.map(item => item.programId),
    ...benefit.benefitEligibility.map(item => item.benefitProgramInstance.program),
    ...benefit.benefitGrants.map(item => item.benefitProgramInstance.program),
  ])
  return [...programs].flatMap(programId => {
    const requested = benefit.requestedBenefits.find(item => item.programId === programId)
    const request = benefit.requestedBenefitsReadUnavailable ? "Request unavailable" : requested ? "Requested" : "No saved request"
    const name = requested?.name.trim() || getBenefitProgramLabel(programId)
    const calculations = benefit.benefitEligibility.filter(item => item.benefitProgramInstance.program === programId)
    // Preserve every returned determination, including other years/revisions and duplicate rows.
    return (calculations.length ? calculations : [null]).map((calculation, index) => ({
      key: `${programId}/${index}`, programId, name, request, calculation,
    }))
  })
}

/** Sum only returned periods, in cents. A missing/invalid cost record is not a zero-dollar bill. */
export function reportedServiceCost(service: Pick<ServiceReceivedDto, "amounts">): number | null {
  if (!service.amounts.length || service.amounts.some(item => !Number.isFinite(item.serviceCostAmount))) return null
  return service.amounts.reduce((cents, item) => cents + Math.round(item.serviceCostAmount * 100), 0) / 100
}
